import axios from 'axios';
import moment from 'moment';
import { ContentType } from '../constants/mediaFormatProfiles';
import { IVFSResponse } from '../services/fileSystemService';
import { apiV2 } from '../lib/baseAxios';
import { FileType, TimeStampedLine } from '../types';
import logger from '../lib/logger';

interface InitializeMultipartUploadOptions {
  teamId: string;
  folderId: string;
  contentType: ContentType;
  source?: string;
}
interface InitializeMultipartUploadResponse {
  fileId: string;
  uploadId: string;
}
export const initializeMultipartUpload = async ({
  teamId,
  folderId,
  contentType,
  source,
}: InitializeMultipartUploadOptions) => {
  // /:teamId/:folderId/upload/multipart
  const response = await apiV2.put<
    IVFSResponse<InitializeMultipartUploadResponse>
  >(`/files/upload/multipart/init/${teamId}/${folderId}`, {
    contentType,
    source,
  });
  return response.data.data;
};

interface CreatePartUploadUrl {
  teamId: string;
  folderId: string;
  fileId: string;
  uploadId: string;
  partNumber: number;
  contentType: ContentType;
}

interface PartUploadUrlResponse {
  uploadUrl: string;
}

export const createPartUploadUrl = async ({
  teamId,
  folderId,
  fileId,
  uploadId,
  partNumber,
  contentType,
}: CreatePartUploadUrl) => {
  // /:teamId/:folderId/upload/multipart/part
  const response = await apiV2.put<IVFSResponse<PartUploadUrlResponse>>(
    `/files/upload/multipart/url/${teamId}/${folderId}/${fileId}/${uploadId}/${partNumber}`,
    {
      contentType,
    },
  );
  console.log('response', response);
  console.log('response.data', response.data);
  return response.data.data.uploadUrl;
};

type FinalizeUploadOptions = {
  teamId: string;
  folderId?: string;
  fileId: string;
  uploadId: string;
  contentType: ContentType;
  notes: TimeStampedLine[];
  title?: string;
  needsConversion?: boolean;
};
interface FinalizeUploadResponseBody {
  file: FileType;
}

export const finalizeUpload = async ({
  teamId,
  folderId,
  fileId,
  uploadId,
  contentType,
  notes,
  title,
  needsConversion,
}: FinalizeUploadOptions) => {
  // /:teamId/:folderId/upload/multipart
  const response = await apiV2.put<IVFSResponse<FinalizeUploadResponseBody>>(
    `/files/upload/multipart/finalize/${teamId}/${folderId}/${fileId}/${uploadId}`,
    {
      file: {
        contentType,
        name:
          title?.trim() ||
          `New Recording ${moment().format('h:mma MMM DD YYYY')}`,
        notes,
        needsConversion,
      },
    },
  );
  return response.data.data.file;
};

export const uploadChunkToS3 = async ({
  uploadUrl,
  chunk,
}: {
  uploadUrl: string;
  chunk: Blob;
}) => {
  if (!uploadUrl) {
    throw new Error('No upload URL provided');
  }
  try {
    await axios.put(uploadUrl, chunk, {
      headers: {
        'Content-Type': chunk.type,
      },
    });
  } catch (error) {
    logger.error('Error uploading chunk to S3', error);
    throw error;
  }
};

/**
 * Upload chunk directly to backend fallback endpoint when S3 upload fails
 */
export const uploadChunkToFallback = async ({
  teamId,
  folderId,
  fileId,
  uploadId,
  partNumber,
  chunk,
}: {
  teamId: string;
  folderId: string;
  fileId: string;
  uploadId: string;
  partNumber: number;
  chunk: Blob;
}) => {
  try {
    logger.info(
      `Using fallback upload for part ${partNumber}, fileId: ${fileId}`,
    );

    // Create FormData to send the chunk as multipart form data
    const formData = new FormData();
    formData.append('chunk', chunk);

    const response = await apiV2.put(
      `/files/upload/multipart/fallback/${teamId}/${folderId}/${fileId}/${uploadId}/${partNumber}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    logger.info(`Successfully uploaded part ${partNumber} using fallback`);
    return response.data;
  } catch (error) {
    logger.error('Error uploading chunk to fallback endpoint', error);
    throw error;
  }
};

// Heartbeat pulse sent while recorder tab is open (recording OR paused)
export const pulseMultipartUpload = async ({
  teamId,
  folderId,
  fileId,
}: {
  teamId: string;
  folderId: string;
  fileId: string;
}): Promise<void> => {
  if (!fileId) {
    throw new Error('pulseMultipartUpload: missing fileId');
  }

  if (!teamId || !folderId) {
    throw new Error('pulseMultipartUpload: missing teamId or folderId');
  }

  const endpoint = `/files/upload/multipart/pulse/${teamId}/${folderId}/${fileId}`;

  try {
    await apiV2.post(endpoint);
  } catch (error) {
    // Retry once after 10 s, then log failure
    logger.warn('Pulse endpoint failed, retrying once in 10 s', error);
    await new Promise((resolve) => setTimeout(resolve, 10_000));
    try {
      await apiV2.post(endpoint);
    } catch (retryError) {
      logger.error('Pulse endpoint failed after retry', retryError);
    }
  }
};

// Pause a multipart upload (called when user explicitly pauses recording)
export const pauseMultipartUpload = async ({
  teamId,
  folderId,
  fileId,
}: {
  teamId: string;
  folderId: string;
  fileId: string;
}): Promise<void> => {
  if (!fileId) {
    throw new Error('pauseMultipartUpload: missing fileId');
  }

  if (!teamId || !folderId) {
    throw new Error('pauseMultipartUpload: missing teamId or folderId');
  }

  const endpoint = `/files/upload/multipart/pause/${teamId}/${folderId}/${fileId}`;

  try {
    await apiV2.post(endpoint);
  } catch (error) {
    logger.error('Pause endpoint failed', error);
    throw error;
  }
};
